import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Group,
  Text,
  Avatar,
  ActionIcon,
  Image,
  Stack,
  Tooltip,
  Menu,
  UnstyledButton,
  Anchor,
  Loader,
  Center,
  Alert,
  Divider,
  Textarea,
  Button,
  Paper,
  SegmentedControl,
  Badge,
} from '@mantine/core';
import {
  IconThumbUp,
  IconThumbUpFilled,
  IconThumbDown,
  IconThumbDownFilled,
  IconDotsVertical,
  IconFlag,
  IconExternalLink,
  IconTrash,
  IconEdit,
  IconAlertCircle,
  IconSend,
  IconBookmark,
  IconBookmarkFilled,
  IconSend2,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useAuth } from '../../context/AuthContext';
import { reviewsService } from '../../services/reviews';
import { commentsService } from '../../services/comments';
import { productsService } from '../../services/products';
import { StarRating } from '../../components/reviews';
import { ReviewContent } from '../../components/reviews/ReviewContent';
import { ImageLightbox } from '../../components/reviews/ImageLightbox';
import { ReportModal } from '../../components/common/ReportModal';
import type { Comment } from '../../types';
import { UserRole } from '../../types';
import { formatRelativeDate, formatFullDate as formatDate } from '../../utils/dateUtils';

export default function ReviewDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { hash } = window.location;

  const [newComment, setNewComment] = useState('');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [votedHelpful, setVotedHelpful] = useState<boolean | null | undefined>(undefined);
  const [helpfulCount, setHelpfulCount] = useState(0);
  const [notHelpfulCount, setNotHelpfulCount] = useState(0);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportCommentId, setReportCommentId] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [voteInitialized, setVoteInitialized] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [commentSort, setCommentSort] = useState<'newest' | 'top'>('newest');

  const { data: review, isLoading: isLoadingReview, error: reviewError } = useQuery({
    queryKey: ['review', id],
    queryFn: () => reviewsService.getById(id!),
    enabled: !!id,
    staleTime: 60_000,
    select: (data) => {
      // Sync vote state when review first loads
      if (!voteInitialized) {
        setVotedHelpful(data.currentUserVotedHelpful);
        setHelpfulCount(data.helpfulCount);
        setNotHelpfulCount(data.notHelpfulCount);
        setIsSaved(data.isSaved ?? false);
        setVoteInitialized(true);
      }
      return data;
    },
  });

  const productId = review?.product?.id;
  const { data: amazonLinkData } = useQuery({
    queryKey: ['product-amazon-link', productId],
    queryFn: () => productsService.getAmazonLink(productId!),
    enabled: !!productId && !review?.product?.amazonAffiliateUrl,
    staleTime: 24 * 60 * 60 * 1000,
  });

  const amazonUrl = review?.product?.amazonAffiliateUrl ?? amazonLinkData?.affiliateUrl;

  const {
    data: commentsData,
    isLoading: isLoadingComments,
    fetchNextPage: fetchMoreComments,
    hasNextPage: hasMoreComments,
    isFetchingNextPage: isFetchingMoreComments,
  } = useInfiniteQuery({
    queryKey: ['comments', id],
    queryFn: ({ pageParam }) => commentsService.getByReviewId(id!, pageParam, 20),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.hasNextPage ? lastPage.page + 1 : undefined,
    enabled: !!id,
    staleTime: 60_000,
  });

  const comments = commentsData?.pages.flatMap((p) => p.items) ?? [];
  const commentsSentinel = useInfiniteScroll(fetchMoreComments, !!hasMoreComments, isFetchingMoreComments);

  // Helper to apply a mapping function over all comment items across pages
  const patchComments = (fn: (c: Comment) => Comment) =>
    queryClient.setQueryData(['comments', id], (old: any) =>
      old ? { ...old, pages: old.pages.map((p: any) => ({ ...p, items: p.items.map(fn) })) } : old
    );

  const appendComment = (comment: Comment) =>
    queryClient.setQueryData(['comments', id], (old: any) => {
      if (!old) return old;
      const pages = [...old.pages];
      const last = { ...pages[pages.length - 1] };
      last.items = [...last.items, comment];
      last.totalCount = last.totalCount + 1;
      pages[pages.length - 1] = last;
      return { ...old, pages };
    });

  const removeComment = (commentId: string) =>
    queryClient.setQueryData(['comments', id], (old: any) =>
      old ? {
        ...old,
        pages: old.pages.map((p: any) => ({
          ...p,
          items: p.items.filter((c: Comment) => c.id !== commentId),
          totalCount: Math.max(0, p.totalCount - 1),
        })),
      } : old
    );

  const submitCommentMutation = useMutation({
    mutationFn: (content: string) => commentsService.create(id!, { content }),
    onSuccess: (comment) => {
      appendComment(comment);
      queryClient.setQueryData(['review', id], (old: typeof review) =>
        old ? { ...old, commentCount: old.commentCount + 1 } : old
      );
      setNewComment('');
      notifications.show({ title: 'Comment added', message: 'Your comment has been posted', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to post comment', color: 'red' });
    },
  });

  const submitReplyMutation = useMutation({
    mutationFn: ({ content, parentCommentId }: { content: string; parentCommentId: string }) =>
      commentsService.create(id!, { content, parentCommentId }),
    onSuccess: (comment) => {
      appendComment(comment);
      setReplyingToId(null);
      setReplyContent('');
      notifications.show({ title: 'Reply added', message: 'Your reply has been posted', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to post reply', color: 'red' });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => commentsService.delete(commentId),
    onSuccess: (_data, commentId) => {
      removeComment(commentId);
      queryClient.setQueryData(['review', id], (old: typeof review) =>
        old ? { ...old, commentCount: old.commentCount - 1 } : old
      );
      notifications.show({ title: 'Deleted', message: 'Comment has been deleted', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to delete comment', color: 'red' });
    },
  });

  const adminDeleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => commentsService.adminDelete(commentId),
    onSuccess: (_data, commentId) => {
      patchComments((c) => c.id === commentId ? { ...c, isDeleted: true, content: 'This comment has been removed.' } : c);
      notifications.show({ title: 'Deleted', message: 'Comment removed by admin', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to delete comment', color: 'red' });
    },
  });

  const editCommentMutation = useMutation({
    mutationFn: ({ commentId, content }: { commentId: string; content: string }) =>
      commentsService.update(commentId, { content }),
    onSuccess: (updated) => {
      patchComments((c) => c.id === updated.id ? updated : c);
      setEditingCommentId(null);
      setEditCommentContent('');
      notifications.show({ title: 'Updated', message: 'Comment updated', color: 'green' });
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update comment', color: 'red' });
    },
  });

  const likeCommentMutation = useMutation({
    mutationFn: ({ commentId, liked }: { commentId: string; liked: boolean }) =>
      liked ? commentsService.unlike(commentId) : commentsService.like(commentId),
    onSuccess: (_data, { commentId, liked }) => {
      patchComments((c) => c.id === commentId
        ? { ...c, likeCount: liked ? c.likeCount - 1 : c.likeCount + 1, currentUserLiked: !liked }
        : c);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update like', color: 'red' });
    },
  });

  const deleteReviewMutation = useMutation({
    mutationFn: () => reviewsService.delete(review!.id),
    onSuccess: () => {
      notifications.show({
        title: 'Deleted',
        message: 'Review has been deleted',
        color: 'green',
      });
      navigate('/');
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete review',
        color: 'red',
      });
    },
  });

  const voteMutation = useMutation({
    mutationFn: (vote: boolean) => reviewsService.voteHelpful(review!.id, vote),
    onSuccess: (result) => {
      setVotedHelpful(result.currentUserVotedHelpful);
      setHelpfulCount(result.helpfulCount);
      setNotHelpfulCount(result.notHelpfulCount);
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update vote', color: 'red' });
    },
  });

  const handleVote = (vote: boolean) => {
    if (!review || !isAuthenticated) {
      notifications.show({ title: 'Login required', message: 'Please log in to vote on reviews', color: 'yellow' });
      return;
    }
    if (voteMutation.isPending) return;
    voteMutation.mutate(vote);
  };

  const saveMutation = useMutation({
    mutationFn: (currentlySaved: boolean) =>
      currentlySaved ? reviewsService.unsave(review!.id) : reviewsService.save(review!.id),
    onSuccess: (_, currentlySaved) => setIsSaved(!currentlySaved),
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed to update saved status', color: 'red' });
    },
  });

  const handleSave = () => {
    if (!isAuthenticated) {
      notifications.show({ title: 'Login required', message: 'Please log in to save reviews', color: 'yellow' });
      return;
    }
    if (saveMutation.isPending) return;
    saveMutation.mutate(isSaved);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notifications.show({ title: 'Link copied', message: 'Review link copied to clipboard', color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to copy link', color: 'red' });
    }
  };

  const handleDelete = () => {
    if (!review || !confirm('Are you sure you want to delete this review?')) return;
    deleteReviewMutation.mutate();
  };

  const handleSubmitComment = () => {
    if (!review || !newComment.trim() || !isAuthenticated) return;
    submitCommentMutation.mutate(newComment.trim());
  };

  const handleDeleteComment = (commentId: string) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    deleteCommentMutation.mutate(commentId);
  };

  const handleSubmitReply = (parentCommentId: string) => {
    if (!replyContent.trim()) return;
    submitReplyMutation.mutate({ content: replyContent.trim(), parentCommentId });
  };

  const isOwner = review ? user?.id === review.user.id : false;
  const isAdmin = user?.role === UserRole.Admin;
  const topLevelComments = comments
    .filter(c => !c.parentCommentId)
    .slice()
    .sort((a, b) => commentSort === 'top'
      ? b.likeCount - a.likeCount
      : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  const getReplies = (commentId: string) => comments.filter(c => c.parentCommentId === commentId);

  const handleAdminDeleteComment = (commentId: string) => {
    if (!confirm('Delete this comment as admin?')) return;
    adminDeleteCommentMutation.mutate(commentId);
  };

  const handleStartEditComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditCommentContent(comment.content);
  };

  const handleLikeComment = (commentId: string, currentlyLiked: boolean) => {
    if (!isAuthenticated) {
      notifications.show({ title: 'Login required', message: 'Please log in to like comments', color: 'yellow' });
      return;
    }
    likeCommentMutation.mutate({ commentId, liked: currentlyLiked });
  };

  const isLoading = isLoadingReview || isLoadingComments;

  useEffect(() => {
    if (!isLoading && hash === '#comments') {
      document.getElementById('comments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isLoading, hash]);

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (reviewError || !review) {
    return (
      <Container fluid px={{ base: 'md', md: 'xl' }} pt="md" pb="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {reviewError ? 'Failed to load review. Please try again.' : 'Review not found'}
          <Button variant="subtle" size="xs" mt="xs" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px={{ base: 'md', md: 'xl' }} pt="md" pb="xl">
<Card shadow="sm" padding="lg" radius="md" withBorder>
        {/* Product header */}
        <Paper bg="var(--mantine-color-default-hover)" p="sm" radius="md" mb="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          {review.product ? (
            <UnstyledButton component={Link} to={`/products/${review.productId}`} style={{ flex: 1, minWidth: 0 }}>
              <Group gap="sm" wrap="nowrap">
                {review.product.imageUrl && (
                  <Image
                    src={review.product.imageUrl}
                    alt={review.product.name}
                    w={64}
                    h={64}
                    radius="sm"
                    fit="contain"
                    style={{ flexShrink: 0, border: '1px solid var(--mantine-color-default-border)' }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <Text size="lg" fw={700} lh={1.3} truncate>
                    {review.product.name}
                  </Text>
                  {review.product.brand && (
                    <Text size="sm" c="dimmed">{review.product.brand}</Text>
                  )}
                </div>
              </Group>
            </UnstyledButton>
          ) : <div />}

          {review.product && amazonUrl && (
            <Anchor
              href={amazonUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              style={{ flexShrink: 0 }}
            >
              <Button
                variant="filled"
                color="orange"
                size="sm"
                leftSection={<IconExternalLink size={14} />}
              >
                Buy on Amazon (paid link)
              </Button>
            </Anchor>
          )}
        </Group>
        </Paper>

        {/* Reviewer attribution */}
        <Group justify="space-between" mb="xs" wrap="wrap" gap="xs">
          <Group gap="xs" wrap="wrap">
            <UnstyledButton component={Link} to={`/users/${review.user.username}`}>
              <Group gap="xs">
                <Avatar src={review.user.profileImageUrl} alt={review.user.username} radius="xl" size="sm" />
                <Text size="sm" fw={500}>{review.user.username}</Text>
              </Group>
            </UnstyledButton>
            <Text size="sm" c="dimmed">·</Text>
            <Tooltip label={formatDate(review.createdAt)} withArrow>
              <Text size="sm" c="dimmed" style={{ cursor: 'default' }}>
                {formatRelativeDate(review.createdAt)}
              </Text>
            </Tooltip>
            {review.user.reviewCount !== undefined && (
              <>
                <Text size="sm" c="dimmed">·</Text>
                <Text size="sm" c="dimmed">{review.user.reviewCount} reviews</Text>
              </>
            )}
          </Group>
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconSend2 size={16} />} onClick={handleCopyLink}>
                Copy link
              </Menu.Item>
              {isOwner ? (
                <>
                  <Menu.Item
                    leftSection={<IconEdit size={16} />}
                    component={Link}
                    to={`/reviews/${review.id}/edit`}
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item
                    leftSection={<IconTrash size={16} />}
                    color="red"
                    onClick={handleDelete}
                  >
                    Delete
                  </Menu.Item>
                </>
              ) : (
                <Menu.Item
                  leftSection={<IconFlag size={16} />}
                  color="red"
                  onClick={() => setReportModalOpen(true)}
                >
                  Report
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>

        {/* Rating */}
        <Group gap="xs" mb="md">
          <StarRating rating={review.rating} size="md" showValue />
        </Group>

        {/* Content */}
        <ReviewContent content={review.content} size="md" mb="lg" />

        {/* Reaction tag chips */}
        {(review.tags?.length ?? 0) > 0 && (
          <Group gap="xs" mb="md">
            {review.tags!.map((tag) => (
              <Badge key={tag.id} variant="outline" color="gray" size="sm">
                {tag.emoji} {tag.label}
              </Badge>
            ))}
          </Group>
        )}

        {/* Images */}
        {review.imageUrls.length > 0 && (
          <Group gap="sm" mb="lg">
            {review.imageUrls.map((url, index) => (
              <Image
                key={index}
                src={url}
                alt={`Review image ${index + 1}`}
                w={150}
                h={150}
                radius="md"
                fit="cover"
                onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
                style={{ cursor: 'zoom-in', border: '1px solid var(--mantine-color-default-border)' }}
              />
            ))}
          </Group>
        )}

        {/* Promotional disclosure */}
        {review.isPromotional && (
          <Alert icon={<IconAlertCircle size={14} />} color="orange" variant="light" py="xs" mb="md">
            <Text size="xs">The reviewer received this product for free or at a discount in exchange for their review.</Text>
          </Alert>
        )}

        {/* Actions */}
        <Group justify="space-between">
          <Group gap="xs">
            <Text size="sm" c="dimmed" fw={500}>Helpful?</Text>
            <Tooltip label={votedHelpful === true ? 'Remove vote' : 'Mark as helpful'}>
              <ActionIcon
                variant="subtle"
                color={votedHelpful === true ? 'blue' : 'gray'}
                size="lg"
                aria-label="Helpful"
                onClick={() => handleVote(true)}
                loading={voteMutation.isPending}
              >
                {votedHelpful === true ? <IconThumbUpFilled size={22} /> : <IconThumbUp size={22} />}
              </ActionIcon>
            </Tooltip>
            <Text size="sm" c={votedHelpful === true ? 'blue' : 'dimmed'}>{helpfulCount}</Text>
            <Tooltip label={votedHelpful === false ? 'Remove vote' : 'Mark as not helpful'}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                aria-label="Not helpful"
                onClick={() => handleVote(false)}
                disabled={voteMutation.isPending}
              >
                {votedHelpful === false ? <IconThumbDownFilled size={22} /> : <IconThumbDown size={22} />}
              </ActionIcon>
            </Tooltip>
            <Text size="sm" c="dimmed">{notHelpfulCount}</Text>
          </Group>
          <Group gap="xs">
            <Tooltip label="Copy link">
              <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleCopyLink}>
                <IconSend2 size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={isSaved ? 'Unsave' : 'Save'}>
              <ActionIcon
                variant="subtle"
                color={isSaved ? 'yellow' : 'gray'}
                size="lg"
                onClick={handleSave}
                loading={saveMutation.isPending}
              >
                {isSaved ? <IconBookmarkFilled size={20} /> : <IconBookmark size={20} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      {/* Comments Section */}
      <Group id="comments" justify="space-between" align="center" mt="xl" mb="sm">
        <Text fw={600} size="sm" c="dimmed">Comments ({comments.length})</Text>
        {topLevelComments.length > 0 && (
          <SegmentedControl
            size="xs"
            value={commentSort}
            onChange={(v) => setCommentSort(v as 'newest' | 'top')}
            data={[
              { label: 'Newest', value: 'newest' },
              { label: 'Top', value: 'top' },
            ]}
          />
        )}
      </Group>
      <Divider mb="xl" />

      {/* Add comment form */}
      {isAuthenticated ? (
        <Group align="flex-start" mb="lg">
          <Avatar src={user?.profileImageUrl} alt={user?.username} radius="xl" />
          <Textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{ flex: 1 }}
            minRows={2}
          />
          <ActionIcon
            variant="filled"
            size="lg"
            onClick={handleSubmitComment}
            loading={submitCommentMutation.isPending}
            disabled={!newComment.trim()}
          >
            <IconSend size={18} />
          </ActionIcon>
        </Group>
      ) : (
        <Alert color="blue" mb="lg">
          <Link to="/login">Log in</Link> to leave a comment
        </Alert>
      )}

      {/* Comments list */}
      <Stack gap="md">
        {topLevelComments.map((comment) => {
          const replies = getReplies(comment.id);
          const isReplying = replyingToId === comment.id;
          const isEditingThis = editingCommentId === comment.id;
          const isMyComment = user?.id === comment.user?.id;
          return (
            <Paper key={comment.id} p="md" withBorder>
              <Group justify="space-between" mb="xs" align="flex-start">
                {comment.isDeleted ? (
                  <Text size="sm" c="dimmed" fs="italic">{comment.content}</Text>
                ) : (
                  <UnstyledButton component={Link} to={`/users/${comment.user.username}`}>
                    <Group gap="sm">
                      <Avatar src={comment.user.profileImageUrl} alt={comment.user.username} radius="xl" size="sm" />
                      <div>
                        <Text size="sm" fw={500}>{comment.user.username}</Text>
                        <Text size="xs" c="dimmed">{formatRelativeDate(comment.createdAt)}</Text>
                      </div>
                    </Group>
                  </UnstyledButton>
                )}
                {!comment.isDeleted && (
                  <Group gap="xs">
                    {/* Like */}
                    <Group gap={4}>
                      <ActionIcon
                        variant="subtle"
                        color={comment.currentUserLiked ? 'blue' : 'gray'}
                        size="sm"
                        onClick={() => handleLikeComment(comment.id, comment.currentUserLiked)}
                      >
                        {comment.currentUserLiked ? <IconThumbUpFilled size={14} /> : <IconThumbUp size={14} />}
                      </ActionIcon>
                      {comment.likeCount > 0 && <Text size="xs" c="dimmed">{comment.likeCount}</Text>}
                    </Group>
                    {/* Reply (owner only) */}
                    {isOwner && (
                      <Button
                        variant="subtle"
                        size="compact-xs"
                        color="gray"
                        onClick={() => {
                          setReplyingToId(isReplying ? null : comment.id);
                          setReplyContent('');
                        }}
                      >
                        {isReplying ? 'Cancel' : 'Reply'}
                      </Button>
                    )}
                    {/* Dots menu */}
                    <Menu shadow="md" position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="sm">
                          <IconDotsVertical size={14} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {isMyComment && (
                          <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => handleStartEditComment(comment)}>
                            Edit
                          </Menu.Item>
                        )}
                        {isMyComment && (
                          <>
                            <Menu.Divider />
                            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleDeleteComment(comment.id)}>
                              Delete
                            </Menu.Item>
                          </>
                        )}
                        {!isMyComment && (
                          <Menu.Item leftSection={<IconFlag size={14} />} color="red" onClick={() => setReportCommentId(comment.id)}>
                            Report
                          </Menu.Item>
                        )}
                        {isAdmin && !isMyComment && (
                          <>
                            <Menu.Divider />
                            <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleAdminDeleteComment(comment.id)}>
                              Delete (Admin)
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                )}
              </Group>

              {/* Comment content or edit form */}
              {!comment.isDeleted && (
                isEditingThis ? (
                  <Group align="flex-start" mt="xs">
                    <Textarea
                      value={editCommentContent}
                      onChange={(e) => setEditCommentContent(e.target.value)}
                      style={{ flex: 1 }}
                      minRows={2}
                      autosize
                    />
                    <Stack gap="xs">
                      <ActionIcon
                        variant="filled"
                        size="md"
                        onClick={() => editCommentMutation.mutate({ commentId: comment.id, content: editCommentContent.trim() })}
                        loading={editCommentMutation.isPending}
                        disabled={!editCommentContent.trim()}
                      >
                        <IconSend size={14} />
                      </ActionIcon>
                      <ActionIcon variant="subtle" size="md" color="gray" onClick={() => setEditingCommentId(null)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Stack>
                  </Group>
                ) : (
                  <Text size="sm">{comment.content}</Text>
                )
              )}

              {/* Replies */}
              {replies.length > 0 && (
                <Stack gap="sm" mt="sm" pl="md" style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}>
                  {replies.map((reply) => (
                    <Group key={reply.id} justify="space-between" align="flex-start">
                      <Group gap="sm" align="flex-start" style={{ flex: 1 }}>
                        <Avatar src={reply.user?.profileImageUrl} alt={reply.user?.username} radius="xl" size="xs" />
                        <div style={{ flex: 1 }}>
                          <Group gap="xs">
                            <Text size="sm" fw={500}>{reply.user?.username}</Text>
                            <Text size="xs" c="dimmed">{formatRelativeDate(reply.createdAt)}</Text>
                          </Group>
                          {reply.isDeleted ? (
                            <Text size="sm" c="dimmed" fs="italic">{reply.content}</Text>
                          ) : (
                            <Text size="sm">{reply.content}</Text>
                          )}
                        </div>
                      </Group>
                      {!reply.isDeleted && (
                        <Group gap={4}>
                          <ActionIcon
                            variant="subtle"
                            color={reply.currentUserLiked ? 'blue' : 'gray'}
                            size="sm"
                            onClick={() => handleLikeComment(reply.id, reply.currentUserLiked)}
                          >
                            {reply.currentUserLiked ? <IconThumbUpFilled size={12} /> : <IconThumbUp size={12} />}
                          </ActionIcon>
                          {reply.likeCount > 0 && <Text size="xs" c="dimmed">{reply.likeCount}</Text>}
                          <Menu shadow="md" position="bottom-end">
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray" size="sm">
                                <IconDotsVertical size={12} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              {user?.id === reply.user?.id ? (
                                <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleDeleteComment(reply.id)}>
                                  Delete
                                </Menu.Item>
                              ) : (
                                <Menu.Item leftSection={<IconFlag size={14} />} color="red" onClick={() => setReportCommentId(reply.id)}>
                                  Report
                                </Menu.Item>
                              )}
                              {isAdmin && user?.id !== reply.user?.id && (
                                <>
                                  <Menu.Divider />
                                  <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleAdminDeleteComment(reply.id)}>
                                    Delete (Admin)
                                  </Menu.Item>
                                </>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      )}
                    </Group>
                  ))}
                </Stack>
              )}

              {/* Reply input */}
              {isReplying && (
                <Group align="flex-start" mt="sm" pl="md" style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}>
                  <Avatar src={user?.profileImageUrl} alt={user?.username} radius="xl" size="xs" />
                  <Textarea
                    placeholder="Write a reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    style={{ flex: 1 }}
                    minRows={2}
                    autosize
                  />
                  <ActionIcon
                    variant="filled"
                    size="lg"
                    onClick={() => handleSubmitReply(comment.id)}
                    loading={submitReplyMutation.isPending}
                    disabled={!replyContent.trim()}
                  >
                    <IconSend size={18} />
                  </ActionIcon>
                </Group>
              )}
            </Paper>
          );
        })}

        {topLevelComments.length === 0 && !isLoadingComments && (
          <Center py="xl">
            <Text c="dimmed">No comments yet. Be the first to comment!</Text>
          </Center>
        )}
        <div ref={commentsSentinel} />
        {isFetchingMoreComments && <Center py="sm"><Loader size="sm" /></Center>}
      </Stack>

      <ReportModal
        opened={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        targetId={review.id}
        targetType="Review"
        targetUserId={review.user.id}
        targetName="this review"
      />

      {reportCommentId && (() => {
        const reportedComment = comments.find(c => c.id === reportCommentId);
        return reportedComment ? (
          <ReportModal
            opened={!!reportCommentId}
            onClose={() => setReportCommentId(null)}
            targetId={reportCommentId}
            targetType="Comment"
            targetUserId={reportedComment.user?.id}
            targetName="this comment"
          />
        ) : null;
      })()}

      <ImageLightbox
        images={review.imageUrls}
        initialIndex={lightboxIndex}
        opened={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </Container>
  );
}
